import React, { useState, useEffect, useRef } from 'react';
import './CreditsPage.css';
import { auth } from '../firebase';
import axios from 'axios';
import config from '../config';
import { toast } from 'react-toastify';
import ImageComparisonSlider from '../components/ImageComparisonSlider';

const CreditsPage = ({ cloudSaveData, unityAuthData, onCloudSaveUpdate }) => {
  // Extract initial credits value from cloudSaveData
  const creditsItem = cloudSaveData?.results?.find(item => item.key === 'credits');
  
  // Ensure we're extracting the number value correctly
  const getCreditsValue = (item) => {
    if (!item) return 0;
    
    // If value is an object with a credits property, use that
    if (typeof item.value === 'object' && item.value !== null && 'credits' in item.value) {
      return item.value.credits;
    }
    
    // Otherwise use the value directly if it's a number, or default to 0
    return typeof item.value === 'number' ? item.value : 0;
  };
  
  const initialCredits = getCreditsValue(creditsItem);
  
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [currentCredits, setCurrentCredits] = useState(initialCredits);
  const [paypalReady, setPaypalReady] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const paypalButtonsRef = useRef({});
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Load PayPal script
  useEffect(() => {
    // Skip if script is already loaded
    const existingScript = document.querySelector('script[src*="paypal"]');
    if (existingScript) {
      console.log('PayPal script already loaded');
      setPaypalReady(true);
      return;
    }
    
    // Debug client ID
    console.log('Loading PayPal with client ID:', config.paypalClientId || 'Missing Client ID');
    
    if (!config.paypalClientId) {
      console.error('PayPal client ID is missing from configuration');
      toast.error('PayPal configuration is missing. Please contact support.');
      return;
    }
    
    // Create script with parameters that help prevent issues
    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${config.paypalClientId}&currency=USD&intent=capture&commit=true`;
    script.async = true;
    
    script.onload = () => {
      console.log('PayPal script loaded successfully');
      setPaypalReady(true);
    };
    
    script.onerror = (error) => {
      console.error('Failed to load PayPal script:', error);
      toast.error('Failed to load PayPal. Please refresh the page.');
      setPaypalReady(false);
    };
    
    document.body.appendChild(script);
    
    // No cleanup needed - leave the script in the DOM to avoid issues
    // PayPal scripts can cause problems when repeatedly added/removed
  }, []);
  
  const creditPacks = [
    { 
      id: 'pack_15_credits', 
      amount: 15, 
      price: '$9.99', 
      pricePerCredit: '$0.67', 
      description: 'Basic package for a quick project', 
      features: [
      ]
    },
    { 
      id: 'pack_30_credits', 
      amount: 30, 
      price: '$14.99', 
      pricePerCredit: '$0.50', 
      savings: '33%', 
      description: 'Great value for multiple renders', 
      bestValue: false,
      features: [
      ]
    },
    { 
      id: 'pack_60_credits', 
      amount: 60, 
      price: '$24.99', 
      pricePerCredit: '$0.42', 
      savings: '40%', 
      description: 'Our best deal for serious designers', 
      bestValue: true,
      features: [
      ]
    },
  ];

  // Setup PayPal buttons for a specific credit pack - simplified approach
  const setupPayPalButtons = (containerId, amount, price) => {
    if (!paypalReady || !window.paypal) {
      console.error('PayPal not ready or window.paypal not available');
      return;
    }
    
    console.log('Setting up PayPal buttons in container', containerId);
    
    // Remove the currency symbol and convert to number
    const priceValue = parseFloat(price.replace(/[^0-9.-]+/g, ''));
    
    // Clear the container first
    const container = document.getElementById(containerId);
    if (!container) {
      console.error('PayPal container not found:', containerId);
      return;
    }
    container.innerHTML = '';
    
    // Render the buttons directly
    window.paypal.Buttons({
      style: {
        layout: 'vertical',
        color: 'blue',
        shape: 'rect',
        label: 'paypal',
        height: 45
      },
      
      createOrder: (data, actions) => {
        console.log('Creating PayPal order');
        return actions.order.create({
          purchase_units: [{
            description: `${amount} Credits for Dream Render`,
            amount: {
              currency_code: 'USD',
              value: priceValue.toFixed(2)
            }
          }]
        });
      },
      
      onApprove: async (data, actions) => {
        try {
          setIsLoading(true);
          
          // Capture the order (finalize the payment)
          const orderDetails = await actions.order.capture();
          console.log('PayPal order captured:', orderDetails);
          
          // Process the credit purchase with our backend
          await processPayPalPurchase(amount, orderDetails.id);
          
          // Close the modal
          setShowPaymentModal(false);
          
        } catch (error) {
          console.error('Error processing PayPal order:', error);
          toast.error(`Payment failed: ${error.message}`);
        } finally {
          setIsLoading(false);
        }
      },
      
      onError: (err) => {
        console.error('PayPal error:', err);
        toast.error('PayPal payment failed. Please try again later.');
        setIsLoading(false);
        setShowPaymentModal(false); // Close the modal on error
      },
      
      onCancel: () => {
        toast.info('Payment cancelled');
        setIsLoading(false);
        setShowPaymentModal(false); // Close the modal on cancel
      }
    }).render(container).catch(err => {
      console.error('Error rendering PayPal buttons:', err);
      toast.error('Failed to load PayPal buttons. Please try again.');
      setIsLoading(false);
    });
  };
  
  // Process PayPal purchase with our backend
  const processPayPalPurchase = async (amount, orderId) => {
    const user = auth.currentUser;
    if (!user) {
      toast.error('Please log in to purchase credits');
      setIsLoading(false);
      return;
    }
    
    try {
      // Get the user's ID token for authentication
      const idToken = await user.getIdToken();
      
      // Make API call to our credit purchase endpoint
      const response = await axios.post(
        `${config.backendUrl}/consumables/purchase`,
        {
          creditsAmount: amount,
          paymentMethod: 'paypal',
          orderId: orderId
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'X-Unity-Token': unityAuthData?.accessToken || '',
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        // Close the payment modal
        setShowPaymentModal(false);
        
        toast.success(`Successfully purchased ${amount} credits!`);
        
        // Ensure we're storing a number, not an object
        const newBalance = response.data.newBalance || response.data.creditsAdded + currentCredits;
        
        // Update the local state
        setCurrentCredits(newBalance);
        
        // Update parent's cloudSaveData if the callback is provided
        if (cloudSaveData?.results && onCloudSaveUpdate) {
          const updatedResults = [...cloudSaveData.results];
          const creditIndex = updatedResults.findIndex(item => item.key === 'credits');
          
          if (creditIndex !== -1) {
            // Update existing credits entry
            updatedResults[creditIndex] = {
              ...updatedResults[creditIndex],
              value: newBalance
            };
          } else {
            // Add new credits entry
            updatedResults.push({
              key: 'credits',
              value: newBalance,
              namespace: '',
              permission: 'Private'
            });
          }
          
          onCloudSaveUpdate({
            ...cloudSaveData,
            results: updatedResults
          });
        }
      } else {
        toast.error('Failed to complete purchase. Please contact support.');
      }
    } catch (error) {
      console.error('Error processing credits purchase:', error);
      toast.error(`Error: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Function to initiate a purchase - opens the PayPal payment modal
  const handlePurchaseCredits = (amount, packId, price) => {
    const user = auth.currentUser;
    if (!user) {
      toast.error('Please log in to purchase credits');
      return;
    }
    
    if (!paypalReady) {
      console.log('PayPal SDK not ready, client ID:', config.paypalClientId);
      toast.error('PayPal is not available. Please refresh the page and try again.');
      return;
    }
    
    // Set the selected pack to show the modal
    setSelectedPack(creditPacks.find(pack => pack.id === packId));
    setShowPaymentModal(true); 
    
    // Buttons will be rendered after the modal is visible
    // using useEffect hook that watches showPaymentModal
  };
  
  // Close the payment modal
  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedPack(null);
    setIsLoading(false);
  };
  
  // Effect hook to render PayPal buttons when the modal appears
  useEffect(() => {
    let timeoutId;
    
    if (showPaymentModal && selectedPack && paypalReady) {
      setIsLoading(true);
      
      // Use a ref to store the timeout ID so we can clear it if needed
      timeoutId = setTimeout(() => {
        // Only attempt to render if the modal is still showing
        if (showPaymentModal && document.getElementById('paypal-button-container')) {
          setupPayPalButtons(
            'paypal-button-container', 
            selectedPack.amount, 
            selectedPack.price
          );
        }
      }, 500);
    }
    
    // Cleanup function to handle component unmounting or dependencies changing
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [showPaymentModal, selectedPack, paypalReady]);

  return (
    <div className="credits-page">
      {/* Banner Section */}
      <div className="banner-container">
        <img 
          src="/images/dream-render.png" 
          alt="Dream Render Credits" 
          className="main-banner"
        />
      </div>

      {/* Example Render Section with Promotional Content */}
      <div className="example-render-container card">
        <div className="example-card-content">
          {/* Left third: promotional text */}
          <div className="hero-content">
            <h2>Transform Your Designs Into Reality</h2>
            <p className="hero-description">
              Dream Render uses advanced AI technology to create stunning photorealistic renders of your house designs.
              Buy credits to generate renders that bring your vision to life with incredible detail and accuracy.
            </p>
            <div className="hero-features">
              <div className="hero-feature">
                <span className="feature-icon">✓</span>
                <span>Photorealistic Quality</span>
              </div>
              <div className="hero-feature">
                <span className="feature-icon">✓</span>
                <span>You frame the shot</span>
              </div>
              <div className="hero-feature">
                <span className="feature-icon">✓</span>
                <span>Interior & Exterior Views</span>
              </div>
              <div className="hero-feature">
                <span className="feature-icon">✓</span>
                <span>Fast Processing Times</span>
              </div>
            </div>
          </div>
          
          {/* Right two-thirds: image comparison slider replaces static image */}
          <div className="example-image-container">
            <div className="comparison-slider-wrapper">
              <ImageComparisonSlider 
                beforeImage="/images/before-render.png"
                afterImage="/images/after-render.png"
                alt="Before and after AI render of house design"
              />
              <p className="comparison-caption">Drag the slider to see the before and after transformation</p>
            </div>
          </div>
        </div>
      </div>
      
      <section className="current-balance-section card">
        <div className="balance-wrapper">
          <div className="balance-header">
            <h2>Your Credit Balance</h2>
          </div>
          <div className="balance-container">
            <p className="credit-amount">{currentCredits}</p>
            <p className="credit-label">Available Credits</p>
          </div>
        </div>
      </section>

      <section className="purchase-section card">
        <h2>Purchase Credits</h2>
        <p className="section-description">
          Choose the credit package that fits your needs. Each credit can be used to generate one high-quality render of your design.
        </p>
        
        <div className="credit-packs-container">
          {creditPacks.map((pack) => (
            <div 
              key={pack.id} 
              className={`credit-pack ${pack.bestValue ? 'best-value' : ''}`}
            >
              {pack.bestValue && <div className="best-value-badge">BEST VALUE</div>}
              {pack.savings && <div className="savings-badge">SAVE {pack.savings}</div>}
              
              <h3 className="pack-title">{pack.amount} Credits</h3>
              <p className="pack-price">{pack.price}</p>
              <p className="price-per-credit">{pack.pricePerCredit} per credit</p>
              
              <div className="pack-features">
                {pack.features.map((feature, index) => (
                  <div key={index} className="pack-feature">
                    <span className="feature-icon">✓</span>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
              
              <button 
                className="purchase-button"
                onClick={() => handlePurchaseCredits(pack.amount, pack.id, pack.price)}
                aria-label={`Purchase ${pack.amount} credits for ${pack.price}`}
                disabled={isLoading}
                tabIndex="0"
              >
                {isLoading ? 'Processing...' : 'Purchase with PayPal'}
              </button>
            </div>
          ))}
        </div>
        
        <div className="purchase-info-container">
          <h3>How Dream Render Credits Work</h3>
          <div className="info-grid">
            <div className="info-item">
              <div className="info-icon">1</div>
              <div className="info-content">
                <h4>Purchase Credits</h4>
                <p>Buy a credit package that fits your project needs</p>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">2</div>
              <div className="info-content">
                <h4>Design Your Space</h4>
                <p>Create and customize your design in the app</p>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">3</div>
              <div className="info-content">
                <h4>Generate Render</h4>
                <p>Choose your quality: Standard (1 credit) or HD (3 credits)</p>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">4</div>
              <div className="info-content">
                <h4>Download & Share</h4>
                <p>Download your render and share it with clients or family</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* PayPal Payment Modal */}
      {showPaymentModal && selectedPack && (
        <div className="modal-overlay">
          <div className="payment-modal">
            <div className="modal-header">
              <h2>Complete Your Purchase</h2>
              <button 
                className="close-button" 
                onClick={closePaymentModal}
                aria-label="Close payment modal"
                tabIndex="0"
              >
                &times;
              </button>
            </div>
            
            <div className="modal-content">
              <div className="purchase-summary">
                <h3>Order Summary</h3>
                <div className="summary-item">
                  <span>Package:</span>
                  <span>{selectedPack.amount} Credits</span>
                </div>
                <div className="summary-item">
                  <span>Price:</span>
                  <span>{selectedPack.price}</span>
                </div>
                <div className="summary-divider"></div>
                <div className="summary-total">
                  <span>Total:</span>
                  <span>{selectedPack.price}</span>
                </div>
              </div>
              
              <div className="payment-options">
                <h3>Select Payment Method</h3>
                <div id="paypal-button-container" className="paypal-button-container">
                  {isLoading && (
                    <div className="paypal-loading">
                      <div className="db-loading-container">
                        <div className="db-loading-ring"></div>
                      </div>
                      <div className="loading-message">Loading payment options...</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <p className="payment-terms">
                By completing this purchase, you agree to our <a href="/terms">Terms of Service</a>.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditsPage;
